import { gql } from "@apollo/client";
import {
  isInspections,
  isNumericCollectionType,
} from "./serviceItemHelpers";

export const SAVE_SERVICE = gql`
  mutation saveService($request: save_service_request!) {
    save_service(request: $request) {
      id
      isNew
      ticket_id
    }
  }
`;

export const SERVICE_DEFINITION_NAME_QUERY = gql`
  query serviceDefinitionNameForParts($service_definition_list_request: service_definition_list_request) {
    service_definition(service_definition_list_request: $service_definition_list_request) {
      edges { id name }
    }
  }
`;

function isInspectionItem(item) {
  return item?.isInspection || isInspections(item);
}

function inspectionPartRows(state) {
  if (state?.parts?.length) return state.parts;
  return [{ quantity: state?.quantity, partNo: state?.partNo }];
}

function collectionPartFields(item, state) {
  const raw = state?.unit != null ? String(state.unit).trim() : "";
  if (!raw) return {};
  if (isNumericCollectionType(item)) {
    const num = Number(raw);
    if (Number.isFinite(num)) return { quantity: num };
  }
  return { description: raw };
}

export function buildSaveServiceParts({ serviceItems, serviceItemStates, savedParts }) {
  const parts = [];
  for (const item of serviceItems || []) {
    if (isInspectionItem(item)) continue;
    const state = serviceItemStates?.[item.id];
    if (!state) continue;
    const saved = (savedParts || []).find((p) => Number(p.service_item_id) === Number(item.id));
    const row = { service_item_id: Number(item.id) };
    if (saved?.id) row.id = saved.id;
    Object.assign(row, collectionPartFields(item, state));
    parts.push(row);
  }
  return parts;
}

export function buildSaveServiceValues({
  serviceItems,
  serviceItemStates,
  serviceComment,
  savedValues,
}) {
  const values = [];
  const trimmed = serviceComment != null ? String(serviceComment).trim() : "";
  const serviceLevel = (savedValues || []).find((v) => !v.service_item_id);
  if (trimmed || serviceLevel) {
    const row = { status: 0, description: trimmed };
    if (serviceLevel?.id) row.id = serviceLevel.id;
    values.push(row);
  }
  for (const item of serviceItems || []) {
    if (!isInspectionItem(item)) continue;
    const state = serviceItemStates?.[item.id];
    if (!state) continue;
    const saved = (savedValues || []).find((v) => Number(v.service_item_id) === Number(item.id));
    const status = state.status === "action" ? 1 : state.status === "ok" ? 2 : 0;
    const row = {
      service_item_id: Number(item.id),
      status,
      description: state.comment != null ? String(state.comment).trim() : "",
    };
    if (saved?.id) row.id = saved.id;
    values.push(row);
  }
  return values;
}

function cleanCell(value) {
  return String(value ?? "").replace(/[|\r\n]+/g, " ").replace(/\s+/g, " ").trim();
}

function orderedActionItems(serviceItems, serviceItemStates) {
  return (serviceItems || [])
    .filter((item) => isInspectionItem(item) && serviceItemStates?.[item.id]?.status === "action")
    .sort((a, b) => {
      const groupA = Number(a.service_group?.id) || 0;
      const groupB = Number(b.service_group?.id) || 0;
      if (groupA !== groupB) return groupA - groupB;
      const sectionCmp = (a.service_section?.name || "").localeCompare(b.service_section?.name || "");
      if (sectionCmp !== 0) return sectionCmp;
      const sequence = (Number(a.sequence) || 0) - (Number(b.sequence) || 0);
      if (sequence !== 0) return sequence;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });
}

export function buildPartsNeededTicket({
  serviceItems,
  serviceItemStates,
  crane,
  craneName,
  technicianName,
  serviceName,
}) {
  const actionItems = orderedActionItems(serviceItems, serviceItemStates);
  if (actionItems.length === 0 || !crane) return null;

  const groups = [];
  for (const item of actionItems) {
    const state = serviceItemStates[item.id] || {};
    const comment = cleanCell(state.comment);
    const itemName = cleanCell(item.name) || "Inspection item";
    const sectionName = cleanCell(item.service_section?.name) || "No section";
    const rows = [];
    for (const part of inspectionPartRows(state)) {
      const qtyStr = part.quantity != null ? String(part.quantity).trim() : "";
      const partNoStr = cleanCell(part.partNo);
      if (!qtyStr && !partNoStr) continue;
      const qtyNum = qtyStr ? Number(qtyStr) : 1;
      const qtyDisplay = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
      rows.push([partNoStr || "—", String(qtyDisplay), comment, itemName].join(" | "));
    }
    if (!rows.length) continue;
    const last = groups[groups.length - 1];
    if (last && last.name === sectionName) last.rows.push(...rows);
    else groups.push({ name: sectionName, rows });
  }

  if (groups.length === 0) return null;

  const when = new Date().toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const summary = [
    `Crane: ${craneName || `Crane #${crane}`}`,
    `Requested by: ${technicianName || "Technician"}`,
    `When: ${when}`,
  ];
  const serviceLabel = cleanCell(serviceName);
  if (serviceLabel) summary.push(`Service: ${serviceLabel}`);

  const body = groups.map((group) => [
    group.name,
    "Part number | Qty | Comment | Requested for",
    ...group.rows,
  ].join("\n")).join("\n\n");

  return {
    name: craneName ? `Parts Needed - ${craneName}` : "Parts Needed - Service",
    description: `${summary.join("\n")}\n\n${body}`,
    ticket_type_id: 2,
    status_id: 1,
    crane_id: Number(crane),
  };
}

export function buildSaveServiceRequest({
  serviceId,
  serviceDefinitionId,
  craneId,
  userId,
  idempotencyKey,
  subscribe,
  serviceItems,
  serviceItemStates,
  serviceComment,
  savedParts,
  savedValues,
  partsNeededTicket,
}) {
  const request = {
    service_definition_id: Number(serviceDefinitionId),
    crane_id: Number(craneId),
    user_id: Number(userId),
    subscribe: Boolean(subscribe),
    parts: buildSaveServiceParts({ serviceItems, serviceItemStates, savedParts }),
    values: buildSaveServiceValues({
      serviceItems,
      serviceItemStates,
      serviceComment,
      savedValues,
    }),
  };
  if (serviceId) request.id = serviceId;
  if (!serviceId && idempotencyKey) request.idempotency_key = idempotencyKey;
  if (partsNeededTicket) request.parts_needed_ticket = partsNeededTicket;
  return request;
}

export function buildOfflineServiceRecord({
  request,
  entityId,
  serviceDefinitionId,
  craneId,
  craneName,
  userId,
  serviceComment,
  serviceItemStates,
  serviceItems,
}) {
  return {
    service_id: entityId ? Number(entityId) : null,
    request,
    service_definition_id: Number(serviceDefinitionId),
    crane_id: Number(craneId),
    crane_name: craneName || "",
    user_id: userId,
    idempotency_key: request?.idempotency_key || null,
    serviceComment: serviceComment || "",
    serviceItemStates: serviceItemStates || {},
    serviceItems: (serviceItems || []).map((item) => ({
      id: item.id,
      name: item.name,
      isInspection: isInspections(item) || Boolean(item.isInspection),
      service_type: item.service_type,
    })),
  };
}

function storedIdempotencyKey(offline) {
  const explicit = offline?.request?.idempotency_key || offline?.idempotency_key;
  if (explicit && String(explicit).trim().length <= 64) return String(explicit).trim();
  const compact = String(offline?.key || "").replace(/^ecrane_offline_service_/, "");
  return `offline-${compact}`.slice(0, 64);
}

export function saveServiceRequestFromOffline(offline) {
  if (offline?.request) {
    const request = { ...offline.request };
    if (offline.service_id) request.id = offline.service_id;
    if (!request.id) request.idempotency_key = storedIdempotencyKey(offline);
    return request;
  }
  return buildSaveServiceRequest({
    serviceId: offline.service_id || undefined,
    serviceDefinitionId: offline.service_definition_id,
    craneId: offline.crane_id,
    userId: offline.user_id ?? offline.technician_id,
    idempotencyKey: storedIdempotencyKey(offline),
    subscribe: !offline.service_id,
    serviceItems: offline.serviceItems || [],
    serviceItemStates: offline.serviceItemStates || {},
    serviceComment: offline.serviceComment || "",
    savedParts: [],
    savedValues: [],
  });
}
